// Facts only. Bot thresholds and classification belong to the cognitive service.
// No DOM text, input values, URL, coordinates or raw typed keys are retained.
export type InteractionEntry = [number, string, string | [number, number], number];
type Emit = (event: string, props: Record<string, string | number>, beacon?: boolean) => void;
const MAX_ENTRIES = 3000;
const MAX_BYTES = 64 * 1024;
const stamp = () => performance.now();
const label = (value: string) => /^[a-zA-Z0-9_.:+-]{1,64}$/.test(value) ? value : "background";

export class InteractionRecorder {
  private entries: InteractionEntry[] = [];
  private bytes = 2;
  private dropped = 0;
  private origin = stamp();
  private started = false;
  private completed = false;
  private abandoned = false;
  private frozen = false;
  private round = 0;
  private roundAt: number | null = null;
  private roundLabel = "trial";
  private moves = 0;
  private path = 0;
  private pointX = 0;
  private pointY = 0;
  private pointerId: number | null = null;
  private hiddenAt: number | null = null;
  private hiddenMs = 0;
  private everHidden = false;
  private pending: { at: number; kind: string; target: string }[] = [];
  private snapshot: ReturnType<InteractionRecorder["payload"]> | null = null;

  constructor(readonly test: string, private emit: Emit) {}

  private elapsed() { return Math.max(0, Math.round(stamp() - this.origin)); }
  private add(kind: string, target: string | [number, number], at = this.elapsed()) {
    if (this.frozen) return;
    const entry: InteractionEntry = [at, kind, target, this.round];
    const size = JSON.stringify(entry).length + (this.entries.length ? 1 : 0); // ASCII-only labels
    if (this.entries.length >= MAX_ENTRIES || this.bytes + size > MAX_BYTES) { this.dropped++; return; }
    this.entries.push(entry);
    this.bytes += size;
  }

  start() {
    if (this.started && !this.completed && !this.abandoned && !this.frozen) this.abandon();
    setInteractionPlaying(this, true);
    // Keep the gesture which invoked start (capture listeners ran first).
    const recent = this.pending.filter(e => stamp() - e.at < 1000);
    this.entries = []; this.bytes = 2; this.dropped = 0;
    this.origin = recent[0]?.at ?? stamp();
    this.started = true; this.completed = false; this.abandoned = false; this.frozen = false;
    this.snapshot = null; this.round = 0; this.roundAt = null;
    this.moves = 0; this.path = 0; this.pointerId = null;
    this.hiddenMs = 0; this.everHidden = document.hidden;
    this.hiddenAt = document.hidden ? stamp() : null;
    this.add("run_start", "start", 0);
    for (const e of recent) this.add(e.kind, e.target, Math.max(0, Math.round(e.at - this.origin)));
    this.pending = [];
    if (document.hidden) this.add("visibility_hidden", "window");
  }

  input(kind: string, target: string) {
    const safe = label(target);
    this.pending.push({ at: stamp(), kind, target: safe });
    if (this.pending.length > 3) this.pending.shift();
    if (this.started) this.add(kind, safe);
  }

  beginRound(target = "trial") {
    if (!this.started || this.frozen) return;
    this.endRound();
    this.round++;
    this.roundAt = this.elapsed();
    this.roundLabel = label(target);
    this.add("round_start", this.roundLabel);
  }

  answer(target = "response") {
    if (!this.started || this.frozen) return;
    if (this.roundAt === null) this.beginRound();
    this.add("answer", label(target));
  }

  private flushMovement() {
    this.add("pointer_summary", [this.moves, Math.round(this.path)]);
    this.moves = 0; this.path = 0; this.pointerId = null;
  }

  endRound() {
    if (this.roundAt === null || this.frozen) return;
    const ms = this.elapsed() - this.roundAt;
    this.flushMovement();
    this.add("round_end", this.roundLabel);
    this.emit("free_test_round_completed", { round: this.round, ms });
    this.roundAt = null;
  }

  move(e: PointerEvent) {
    if (!this.started || this.frozen) return;
    this.moves++;
    if (this.pointerId === e.pointerId) this.path += Math.hypot(e.clientX - this.pointX, e.clientY - this.pointY);
    // The current point is overwritten, never serialized. No DOM work here.
    this.pointX = e.clientX; this.pointY = e.clientY; this.pointerId = e.pointerId;
  }

  lifecycle(kind: string) {
    if (!this.started) return;
    if (kind === "visibility_hidden" && this.hiddenAt === null) {
      this.everHidden = true; this.hiddenAt = stamp();
    } else if (kind === "visibility_visible" && this.hiddenAt !== null) {
      this.hiddenMs += stamp() - this.hiddenAt; this.hiddenAt = null;
    }
    this.add(kind, "window");
  }

  private payload() {
    const duration = this.elapsed();
    const side = Math.max(screen.width || 0, screen.height || 0);
    return {
      interaction_log: this.entries.slice(),
      client_signals: {
        version: 1, webdriver: navigator.webdriver === true,
        languages_count: navigator.languages?.length ?? 0,
        hardware_concurrency: navigator.hardwareConcurrency ?? 0,
        screen_bucket: side === 0 ? "unknown" : side <= 900 ? "small" : side <= 1600 ? "medium" : "large",
        touch_support: navigator.maxTouchPoints > 0 || "ontouchstart" in window,
        timezone_offset: new Date().getTimezoneOffset(),
        ever_hidden: this.everHidden,
        hidden_ms: Math.min(duration, Math.round(this.hiddenMs + (this.hiddenAt === null ? 0 : stamp() - this.hiddenAt))),
        duration_ms: duration, dropped_entries: this.dropped,
      },
    };
  }

  scorePayload() {
    if (this.snapshot) return this.snapshot; // a failed score POST retries the same run
    if (!this.started) this.start();
    this.endRound();
    this.flushMovement();
    this.add("run_end", "score");
    this.snapshot = this.payload();
    this.frozen = true;
    setInteractionPlaying(this, false);
    return this.snapshot;
  }
  complete() { this.completed = true; setInteractionPlaying(this, false); }
  errorContext() {
    return { test_code: this.test, phase: this.completed ? "result" : this.abandoned ? "abandoned" : this.frozen ? "scoring" : this.started ? "playing" : "intro", round: this.round };
  }
  action(action: string) {
    if (this.completed) this.emit("free_test_result_action", { action }, true);
  }
  abandon() {
    if (!this.started || this.completed || this.abandoned) return;
    this.abandoned = true;
    setInteractionPlaying(this, false);
    this.emit("free_test_abandoned", { last_round: this.round, ms: this.elapsed() }, true);
  }
}

let current: InteractionRecorder | null = null;
let playing = false;
const phaseListeners = new Set<(playing: boolean) => void>();
/** Analytics and error SDK work belongs outside a measured game run. */
export function interactionPlaying(): boolean { return playing; }
export function onInteractionPlaying(listener: (playing: boolean) => void): () => void {
  phaseListeners.add(listener);
  return () => { phaseListeners.delete(listener); };
}
function setInteractionPlaying(recorder: InteractionRecorder, value: boolean) {
  if (recorder !== current || playing === value) return;
  playing = value;
  for (const listener of phaseListeners) listener(value);
}
/** Categorical state only: never interaction entries, answers, text or pointer coordinates. */
export function interactionErrorContext(): Record<string, string | number> { return current?.errorContext() ?? {}; }
export const interactions = {
  start: (test: string) => { if (current?.test === test) current.start(); },
  pause: () => { if (current) setInteractionPlaying(current, false); },
  round: (target = "trial") => current?.beginRound(target),
  answer: (target = "response") => current?.answer(target),
  endRound: () => current?.endRound(),
  score: (test: string) => current?.test === test ? current.scorePayload() : {},
  complete: (test: string) => { if (current?.test === test) current.complete(); },
};

/** Install once at the shared TestPage boundary, remove on route changes. */
export function attachInteractions(root: HTMLElement, test: string, emit: Emit) {
  const recorder = new InteractionRecorder(test, emit);
  current = recorder;
  const removers: (() => void)[] = [];
  const listen = (on: EventTarget, type: string, fn: (e: Event) => void) => {
    on.addEventListener(type, fn, { capture: true, passive: true });
    removers.push(() => on.removeEventListener(type, fn, true));
  };
  const hit = (e: Event) => {
    const node = e.target instanceof Element ? e.target : null;
    if (!node || !root.contains(node)) return "background";
    const surface = node.closest("[data-hit-surface]");
    if (surface && e instanceof MouseEvent) {
      for (const candidate of surface.querySelectorAll<HTMLElement>("[data-hit-target]")) {
        const r = candidate.getBoundingClientRect();
        if (Math.hypot((e.clientX - (r.left + r.width / 2)) / (r.width / 2),
            (e.clientY - (r.top + r.height / 2)) / (r.height / 2)) <= 1) return candidate.dataset.interaction ?? "background";
      }
    }
    // Labels are supplied in source, never from button/answer text or input values.
    const control = node.closest("button,a,summary");
    return node.closest<HTMLElement>("[data-interaction]")?.dataset.interaction
      ?? control?.closest<HTMLElement>("[data-result-action]")?.dataset.resultAction ?? "background";
  };
  for (const kind of ["pointerdown", "pointerup", "click"]) {
    listen(window, kind, event => {
      const e = event as PointerEvent;
      const pointer = ["mouse", "touch", "pen"].includes(e.pointerType) ? e.pointerType : "unknown";
      const target = hit(e);
      recorder.input(`${kind}:${pointer}`, target);
      if (kind === "click") {
        const node = e.target instanceof Element ? e.target.closest("button,a,summary")?.closest<HTMLElement>("[data-result-action]") : null;
        if (node && root.contains(node)) {
          recorder.action(node.dataset.resultAction!);
          // Sharing a score/seed/profile also creates the existing challenge link.
          if (node.dataset.challengeShare === "true") recorder.action("challenge");
        }
      }
    });
  }
  listen(window, "pointermove", event => recorder.move(event as PointerEvent));
  listen(window, "keydown", event => {
    const e = event as KeyboardEvent;
    const target = hit(e);
    if (e.target instanceof Element && e.target.hasAttribute("data-numeric-answer") && /^[0-9]$/.test(e.key)) {
      recorder.input("key:answer", `key:${e.key}`);
      return;
    }
    const input = e.target instanceof Element && e.target.closest("input,textarea,[contenteditable='true']");
    // An explicit answer-key annotation maps a physical key to an option. Text
    // entry is always categorical, even if the typed character is a digit.
    const answer = !input && !e.ctrlKey && !e.metaKey && !e.altKey && !e.isComposing
      ? Array.from(root.querySelectorAll<HTMLElement>("[data-answer-keys]")).find(el =>
          !el.matches(":disabled") && el.getClientRects().length > 0 &&
          el.dataset.answerKeys?.split(" ").some(k => k === e.code || k.toLowerCase() === e.key.toLowerCase() ||
            (/^[a-z]$/i.test(k) && e.code === `Key${k.toUpperCase()}`)))
      : undefined;
    const category = e.key.length === 1 ? "character" : /^(Backspace|Delete)$/.test(e.key) ? "editing"
      : /^(Arrow|Home|End|Page|Tab)/.test(e.key) ? "navigation"
      : /^(Control|Alt|Meta|Shift|CapsLock)$/.test(e.key) ? "modifier"
      : /^(Enter|Escape)$/.test(e.key) ? "activation" : "other";
    const focusedAnswer = !input && (e.key === "Enter" || e.code === "Space") && /^(option|cell|peg|deck|note|key|response)(:|$)/.test(target);
    recorder.input(`key:${e.repeat ? "repeat" : answer || focusedAnswer ? "answer" : category}`, answer?.dataset.interaction ?? target);
  });
  listen(document, "visibilitychange", () => recorder.lifecycle(document.hidden ? "visibility_hidden" : "visibility_visible"));
  for (const kind of ["blur", "focus", "resize"]) listen(window, kind, e => {
    if (e.target === window) recorder.lifecycle(kind);
  });
  listen(window, "orientationchange", () => recorder.lifecycle("orientation"));
  listen(window, "pagehide", () => { recorder.lifecycle("pagehide"); recorder.abandon(); });
  return () => {
    recorder.abandon();
    setInteractionPlaying(recorder, false);
    removers.forEach(remove => remove());
    if (current === recorder) current = null;
  };
}
